import { useEffect, useState } from 'react';
import { Button } from '@evw/ui';
import { useAuth } from '../auth/auth-context';
import { supabase } from '../../lib/supabase';
import { useMemberships } from '../tenancy/useMemberships';
import { OrganisationProfile } from './OrganisationProfile';
import { LocationsPage } from './LocationsPage';
import { StaffPage } from './StaffPage';
import { ServicesPage } from './ServicesPage';
import { SchedulingPage } from '../scheduling/SchedulingPage';
import { CustomersPage } from './CustomersPage';
import { PaymentsPage } from './PaymentsPage';

type Section =
  'profile' | 'locations' | 'staff' | 'services' | 'customers' | 'scheduling' | 'payments';

export function AdminApp() {
  const { session } = useAuth();
  const memberships = useMemberships(session!.user.id);
  const [organisationId, setOrganisationId] = useState('');
  const [section, setSection] = useState<Section>(
    window.location.pathname.startsWith('/admin/payments') ? 'payments' : 'profile'
  );

  useEffect(() => {
    if (!organisationId && memberships.data?.[0])
      setOrganisationId(memberships.data[0].organisation_id);
    if (
      organisationId &&
      memberships.data &&
      !memberships.data.some((item) => item.organisation_id === organisationId)
    )
      setOrganisationId('');
  }, [memberships.data, organisationId]);

  if (memberships.isLoading)
    return (
      <main className="centered">
        <p>Loading your organisations…</p>
      </main>
    );
  if (memberships.error)
    return (
      <main className="centered">
        <p className="error">Unable to load memberships: {memberships.error.message}</p>
      </main>
    );
  if (!memberships.data?.length)
    return (
      <main className="centered">
        <div>
          <h1>No organisation access</h1>
          <p className="muted">Ask an organisation owner to add an active membership.</p>
          <Button onClick={() => void supabase.auth.signOut()}>Log out</Button>
        </div>
      </main>
    );
  const membership = (memberships.data.find((item) => item.organisation_id === organisationId) ??
    memberships.data[0])!;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div>
          <span className="eyebrow">EVW Business Platform</span>
          <strong>{membership.organisation.name}</strong>
        </div>
        <Button className="button-ghost" onClick={() => void supabase.auth.signOut()}>
          Log out
        </Button>
      </header>
      <div className="workspace">
        <aside>
          <label className="organisation-picker">
            Organisation
            <select
              value={membership.organisation_id}
              onChange={(event) => {
                setOrganisationId(event.target.value);
                setSection('profile');
              }}
            >
              {memberships.data.map((item) => (
                <option value={item.organisation_id} key={item.organisation_id}>
                  {item.organisation.name}
                </option>
              ))}
            </select>
          </label>
          <nav aria-label="Administration">
            <NavButton active={section === 'profile'} onClick={() => setSection('profile')}>
              Profile
            </NavButton>
            <NavButton active={section === 'locations'} onClick={() => setSection('locations')}>
              Locations
            </NavButton>
            <NavButton active={section === 'staff'} onClick={() => setSection('staff')}>
              Staff
            </NavButton>
            <NavButton active={section === 'services'} onClick={() => setSection('services')}>
              Services
            </NavButton>
            <NavButton active={section === 'customers'} onClick={() => setSection('customers')}>
              Customers
            </NavButton>
            <NavButton active={section === 'scheduling'} onClick={() => setSection('scheduling')}>
              Scheduling
            </NavButton>
            {['owner', 'admin'].includes(membership.role) ? (
              <NavButton active={section === 'payments'} onClick={() => setSection('payments')}>
                Payments
              </NavButton>
            ) : null}
          </nav>
          <p className="signed-in">
            Signed in as
            <br />
            <strong>{session!.user.email}</strong>
          </p>
        </aside>
        <main className="content">
          {section === 'profile' ? (
            <OrganisationProfile organisation={membership.organisation} role={membership.role} />
          ) : null}
          {section === 'locations' ? (
            <LocationsPage organisationId={membership.organisation_id} role={membership.role} />
          ) : null}
          {section === 'staff' ? (
            <StaffPage organisationId={membership.organisation_id} role={membership.role} />
          ) : null}
          {section === 'services' ? (
            <ServicesPage
              organisationId={membership.organisation_id}
              role={membership.role}
              currency={membership.organisation.default_currency}
            />
          ) : null}
          {section === 'scheduling' ? (
            <SchedulingPage organisationId={membership.organisation_id} role={membership.role} />
          ) : null}
          {section === 'customers' ? (
            <CustomersPage organisationId={membership.organisation_id} role={membership.role} />
          ) : null}
          {section === 'payments' && ['owner', 'admin'].includes(membership.role) ? (
            <PaymentsPage organisationId={membership.organisation_id} />
          ) : null}
        </main>
      </div>
    </div>
  );
}

function NavButton({
  active,
  children,
  onClick
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button className={active ? 'nav-button active' : 'nav-button'} onClick={onClick}>
      {children}
    </button>
  );
}
